import React, { useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  Legend,
  Cell,
} from "recharts";
import {
  Droplets,
  TrendingDown,
  MapPin,
  AlertTriangle,
  Clock,
  Waves,
  Search,
  Filter,
  Download,
  Info,
  BarChart3,
  MousePointer2,
  Activity,
} from "lucide-react";
import { useSelector } from "react-redux";

// --- NRW Analytics Mock Data ---
const NRW_DATA = {
  kpis: {
    systemInput: "450.0", // k m³
    billedVolume: "315.0", // k m³
    nrwPercentage: "30", // %
    highLossDMAs: "04",
    status: "Requires attention",
  },
  dmaPerformance: [
    { zone: "DMA-01", loss: 12, billing: 88 },
    { zone: "DMA-02", loss: 35, billing: 65 },
    { zone: "DMA-03", loss: 22, billing: 78 },
    { zone: "DMA-04", loss: 45, billing: 55 },
    { zone: "DMA-05", loss: 18, billing: 82 },
  ],
  nightFlow: [
    { time: "00:00", flow: 40 },
    { time: "01:00", flow: 38 },
    { time: "02:00", flow: 37 },
    { time: "03:00", flow: 42 },
    { time: "04:00", flow: 45 },
    { time: "05:00", flow: 55 },
  ],
  pressureNrw: [
    { pressure: 1.5, nrw: 10 },
    { pressure: 2.5, nrw: 18 },
    { pressure: 3.5, nrw: 28 },
    { pressure: 4.5, nrw: 42 },
    { pressure: 5.5, nrw: 55 },
  ],
};

const NRWDashboard = () => {
  const themeMode = useSelector((state) => state.theme?.mode);
  const cardBg = themeMode ? "bg-slate-900/80" : "bg-white";
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";

  return (
    <div className={`w-full ${bgColorClass} ${textColorClass} h-full`}>
      {/* --- Header Section --- */}

      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <Waves size={16} />
          </p>
          <p className="font-semibold">NRW Analytics Dashboard</p>
        </div>
        
      </div>

      <div className=" h-[calc(100vh-50px)] pb-10 pt-5 sidebar_scroll overflow-y-auto sidebar_scroll">
        <div className="px-3 md:px-5 lg:px-12">
          {/* --- KPI Grid (As per image_b4fbe8.png) --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <NRWCard
              label="System Input"
              value={NRW_DATA.kpis.systemInput}
              unit="k m³"
              icon={<Droplets size={20} />}
              color="bg-blue-500"
              cardBg={cardBg}
              borderThem={borderThem}
            />
            <NRWCard
              label="Billed Volume"
              value={NRW_DATA.kpis.billedVolume}
              unit="k m³"
              icon={<Activity size={20} />}
              color="bg-green-500"
              cardBg={cardBg}
              borderThem={borderThem}
              trend="Within target"
            />
            <NRWCard
              label="NRW %"
              value={NRW_DATA.kpis.nrwPercentage}
              unit="%"
              icon={<TrendingDown size={20} />}
              color="bg-emerald-500"
              cardBg={cardBg}
              borderThem={borderThem}
              trend="Within target"
              isSuccess
            />
            <NRWCard
              label="High Loss DMAs"
              value={NRW_DATA.kpis.highLossDMAs}
              unit="Zones"
              icon={<MapPin size={20} />}
              color="bg-red-500"
              cardBg={cardBg}
              borderThem={borderThem}
              trend="Requires attention"
              isCritical
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* --- Report 1: NRW by DMA Zone --- */}
            <div
              className={`lg:col-span-8 ${cardBg} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-semibold text-base tracking-tight">
                    NRW by DMA Zone
                  </h3>
                  <p className="text-xs opacity-50 font-bold tracking-widest text-blue-500">
                    Water loss percentage per district
                  </p>
                </div>
                <BarChart3 size={24} className="opacity-20 text-blue-500" />
              </div>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={NRW_DATA.dmaPerformance} margin={{ top: 20 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="zone"
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                      unit="%"
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend iconType="circle" verticalAlign="top" height={36} />
                    <Bar
                      name="Water Loss %"
                      dataKey="loss"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    >
                      {NRW_DATA.dmaPerformance.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={entry.loss > 30 ? "#ef4444" : "#3b82f6"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* --- Report 2: Night Flow Analysis (Logical Insight) --- */}
            <div
              className={`lg:col-span-4 ${cardBg} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="mb-6">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Clock size={20} className="text-purple-500" /> Night Flow
                  Analysis
                </h3>
                <p className="text-xs opacity-50 font-medium">
                  Identifying background leakage
                </p>
              </div>
              <div className="h-[200px] mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={NRW_DATA.nightFlow}>
                    <defs>
                      <linearGradient
                        id="colorFlow"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#8b5cf6"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#8b5cf6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="flow"
                      stroke="#8b5cf6"
                      fillOpacity={1}
                      fill="url(#colorFlow)"
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="p-4 bg-slate-500/5 rounded-xl border border-slate-500/10">
                <div className="flex items-start gap-3">
                  <Info size={16} className="text-blue-500 mt-1" />
                  <div>
                    <p className="text-xs font-semibold uppercase text-blue-500 mb-1">
                      Observation
                    </p>
                    <p className="text-[11px] font-bold leading-relaxed opacity-80 italic">
                      Minimum Night Flow (MNF) is higher than 35 m³/h in DMA-04.
                      Potential hidden pipe burst or unauthorized commercial
                      usage detected.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Report 3: Pressure vs NRW Correlation --- */}
            <div
              className={`lg:col-span-12 ${cardBg} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="font-semibold text-base italic">
                    Pressure vs NRW Correlation
                  </h3>
                  <p className="text-xs opacity-50 font-medium tracking-tighter">
                    Identifying pressure-related losses
                  </p>
                </div>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={NRW_DATA.pressureNrw}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="pressure"
                      fontSize={12}
                      label={{
                        value: "Pressure (bar)",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      fontSize={12}
                      label={{
                        value: "NRW %",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="nrw"
                      name="Water Loss %"
                      fill="#3b82f6"
                      fillOpacity={0.1}
                      stroke="#3b82f6"
                      strokeWidth={2}
                    />
                    <Line
                      type="step"
                      dataKey="nrw"
                      stroke="#ef4444"
                      strokeWidth={3}
                      dot={{ r: 6 }}
                      name="Loss Gradient"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* --- Logical Summary Table --- */}
          <div
            className={`mt-8 ${cardBg} border ${borderThem} rounded-2xl overflow-hidden shadow-sm`}
          >
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-semibold text-base tracking-widest opacity-60">
                NRW Strategic Report Table
              </h3>
              <span className="bg-red-500/10 text-red-500 px-3 py-1 rounded-full text-[10px] font-black uppercase">
                Action List
              </span>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-500/5 text-[10px] uppercase font-black opacity-40">
                <tr>
                  <th className="p-4">Report</th>
                  <th className="p-4">Logic</th>
                  <th className="p-4">Insight / Strategy</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <ReportRow
                  report="Energy vs Flow"
                  logic="kWh / m³"
                  insight="High cost water detected in South- Narwal"
                  action="Optimize Pump Curve"
                />
                <ReportRow
                  report="Efficiency Deviation"
                  logic="Actual vs Design"
                  insight="Underperforming pumps in Zone-B"
                  action="Inspect Impeller"
                />
                <ReportRow
                  report="High Loss DMAs"
                  logic="NRW > 25%"
                  insight="Step testing required in DMA-02"
                  action="Initiate Acoustic Logging"
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Reusable Sub-Components ---

const NRWCard = ({
  label,
  value,
  unit,
  icon,
  color,
  trend,
  cardBg,
  borderThem,
  isCritical,
  isSuccess,
}) => (
  <div
    className={`${cardBg} border ${borderThem} p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group`}
  >
    <div className="flex justify-between items-start mb-4">
      <div
        className={`p-2 rounded-xl text-white ${color} shadow-lg shadow-current/20`}
      >
        {icon}
      </div>
      {trend && (
        <span
          className={`text-[10px] font-black uppercase px-2 py-1 rounded-md ${
            isCritical
              ? "bg-red-500/10 text-red-500"
              : isSuccess
              ? "bg-green-500/10 text-green-500"
              : "bg-blue-500/10 text-blue-500"
          }`}
        >
          {trend}
        </span>
      )}
    </div>
    <div className="flex items-baseline gap-1 mb-1">
      <p className="text-xl font-black tracking-tighter">{value}</p>
      <p className="text-[10px] font-bold opacity-40 uppercase">{unit}</p>
    </div>
    <p className="text-xs font-bold opacity-60 uppercase tracking-widest leading-none">
      {label}
    </p>
  </div>
);

const ReportRow = ({ report, logic, insight, action }) => (
  <tr className="border-t border-slate-500/5 hover:bg-slate-500/5 transition-colors group">
    <td className="p-4 font-semibold">{report}</td>
    <td className="p-4 font-mono text-blue-600 font-bold">{logic}</td>
    <td className="p-4 opacity-70 italic font-medium">"{insight}"</td>
    <td className="p-4 text-right text-xs">
      <button className="font-semibold text-blue-500 uppercase underline-offset-4 hover:underline flex items-center justify-end gap-1 ml-auto">
        <MousePointer2 size={12} /> {action}
      </button>
    </td>
  </tr>
);

export default NRWDashboard;
